import { useMemo, useState } from 'react';
import {
  QueryBuilder,
  FilterBar,
  useQueryBuilderContext,
  parsedQueryToAntlr,
  isBooleanLike,
  isNumberLike,
} from '@gonfalon/dataviz-experimental/QueryBuilder';
import type { FieldOptions, RelationalFieldOption } from '@gonfalon/dataviz-experimental/types';
import { enableDatavizTracesTable } from '@gonfalon/dogfood-flags/dogfood-flags';
import { toTraces } from '@gonfalon/navigator/navigators';
import { QueryProvider as ObservabilityQueryProvider } from '@gonfalon/observability-graphql/components/QueryProvider/QueryProvider';
import { getSuggestions } from '@gonfalon/openapi/operations/getSuggestions';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { LinkButton } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';

import { TraceDataPreviewTable } from './TraceDataPreviewTable';

import styles from './MetricSourceQueryBuilder.module.css';

// Relational expression types offered in the LogicParen type cell. Mirrors the
// QueryBuilder.stories.tsx Observability Search preset so the production form
// surfaces the same options as the stories playground. The `value` field
// matches the ANTLR_TO_LEZER_RELATIONAL_PREFIX keys (camelCase).
const TRACE_RELATIONAL_FIELD_OPTIONS: RelationalFieldOption[] = [
  {
    value: 'anySpan',
    displayName: 'Any span',
    description: 'Match spans where any span in the same trace satisfies the filter.',
  },
  {
    value: 'anySpan2',
    displayName: 'Any span 2',
    description:
      'Second independent any span condition. Use when you need multiple separate span filters on the same trace.',
  },
  {
    value: 'anySpan3',
    displayName: 'Any span 3',
    description:
      'Third independent any span condition. Use when you need multiple separate span filters on the same trace.',
  },
  {
    value: 'noSpan',
    displayName: 'No span',
    description: 'Match spans where no span in the same trace satisfies the filter.',
  },
];

// Suggestions are fetched and transformed into FieldOptions here so the
// resulting array can be shared by both QueryBuilderFilters (for autocomplete)
// and TraceDataPreviewTable (where buildDatavizQuery uses `valueType` to emit
// the right payload shape for present-sentinel filters on boolean fields).
function useTraceFieldOptions(projectKey: string): FieldOptions[] {
  const [now] = useState(() => Date.now());
  const sevenDaysAgo = now - 604_800_000;
  const { data } = useQuery({
    queryFn: async () => {
      const payload = {
        projectKey,
        dataSource: {
          domain: 'observability',
          kind: 'traces',
          timeRange: {
            from: sevenDaysAgo,
            to: now,
            timezone: 'America/New_York',
          },
        },
        suggestFields: {
          limit: 100, // max
          withValues: {
            limit: 100,
          },
        },
      };
      return getSuggestions({ body: payload });
    },
    queryKey: ['suggestions', projectKey],
  });

  return useMemo<FieldOptions[]>(() => {
    const items = data?.data?.items ?? [];
    return items.map((item) => {
      const sampleValues = item.sampleValues ?? [];
      const mappedSampleValues = sampleValues.map((sv) => ({
        value: String(sv.value),
        approxCount: sv.count,
      }));

      const allNumber = sampleValues.length > 0 && sampleValues.every((sv) => isNumberLike(sv.value));
      const allBoolean = sampleValues.length > 0 && sampleValues.every((sv) => isBooleanLike(sv.value));
      const allString = sampleValues.length > 0 && sampleValues.every((sv) => typeof sv.value === 'string');

      if (allNumber) {
        return {
          field: item.field,
          valueType: 'number',
          allowedOperators: ['=', '!=', '>', '>=', '<', '<='],
          sampleValues: mappedSampleValues,
          allowCustom: true,
        } satisfies FieldOptions;
      } else if (allBoolean) {
        return {
          field: item.field,
          valueType: 'boolean',
          allowedOperators: ['=', '!='],
          sampleValues: mappedSampleValues,
          allowCustom: true,
        } satisfies FieldOptions;
      } else if (allString) {
        return {
          field: item.field,
          valueType: 'string',
          multi: true,
          allowedOperators: ['=', '!=', '//', '!//'],
          sampleValues: mappedSampleValues,
          allowCustom: true,
        } satisfies FieldOptions;
      }
      return { field: item.field, sampleValues: mappedSampleValues };
    });
  }, [data]);
}

const QueryBuilderFilters: React.FC<{
  isEditable: boolean;
  inputId?: string;
  fieldOptions: FieldOptions[];
}> = ({ isEditable, inputId, fieldOptions }) => {
  return (
    <div className={styles.queryBuilderFilters}>
      <FilterBar
        readonly={!isEditable}
        fieldOptions={fieldOptions}
        inputId={inputId}
        showSearchIcon={isEditable}
        relationalFieldOptions={TRACE_RELATIONAL_FIELD_OPTIONS}
      />
    </div>
  );
};

const ViewTracesLink = () => {
  const projectKey = useProjectKey();
  // Build the link from the *committed* query so it matches what the preview
  // table renders and what gets saved as the metric's traceQuery. Using the live
  // `parsedQuery` would open the traces page with uncommitted, mid-edit filters
  // that aren't reflected in the preview and won't be persisted.
  const { parsedCommittedQuery } = useQueryBuilderContext();

  return (
    <div className={styles.runQueryButton}>
      <LinkButton
        href={toTraces({ projectKey }, { search: { query: parsedQueryToAntlr(parsedCommittedQuery) } })}
        target="_blank"
        rel="noreferrer"
      >
        View traces <Icon name="link-external" size="small" />
      </LinkButton>
    </div>
  );
};

const MetricSourceQueryBuilderInner: React.FC<{
  isEditable: boolean;
  inputId?: string;
  wrapFilters?: (filters: React.ReactNode) => React.ReactNode;
}> = ({ isEditable, inputId, wrapFilters }) => {
  const projectKey = useProjectKey();
  const { parsedCommittedQuery } = useQueryBuilderContext();
  const displayDatavizTracePreview = enableDatavizTracesTable();
  const fieldOptions = useTraceFieldOptions(projectKey);
  const filters = <QueryBuilderFilters isEditable={isEditable} inputId={inputId} fieldOptions={fieldOptions} />;

  return (
    <>
      {wrapFilters ? wrapFilters(filters) : filters}
      {isEditable && <ViewTracesLink />}
      {displayDatavizTracePreview && (
        <TraceDataPreviewTable
          isEditable={isEditable}
          submittedQuery={parsedCommittedQuery}
          fieldOptions={fieldOptions}
        />
      )}
    </>
  );
};

interface MetricSourceQueryBuilderProps {
  defaultValue?: string;
  isEditable?: boolean;
  inputId?: string;
  onChange?: (value: string) => void;
  wrapFilters?: (filters: React.ReactNode) => React.ReactNode;
  /** Forwarded to the underlying QueryBuilder root element. */
  className?: string;
}

export const MetricSourceQueryBuilder: React.FC<MetricSourceQueryBuilderProps> = ({
  defaultValue,
  isEditable = true,
  inputId,
  onChange,
  wrapFilters,
  className,
}) => {
  return (
    <ObservabilityQueryProvider>
      <QueryBuilder
        key={defaultValue}
        defaultValue={defaultValue}
        onChange={(event) => onChange?.(parsedQueryToAntlr(event.tokens))}
        className={className}
      >
        <MetricSourceQueryBuilderInner isEditable={isEditable} inputId={inputId} wrapFilters={wrapFilters} />
      </QueryBuilder>
    </ObservabilityQueryProvider>
  );
};
